import { useState } from 'react';
import { Separator, YStack } from 'tamagui';

import { Picker } from '../../components/inputs/Picker';
import { IconSelectDropdown } from '../../components/select/IconSelectDropdown';
import { SelectDropdown } from '../../components/select/SelectDropdown';
import {
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
} from '../../components/text/Typography';

const OPTIONS = [
    { value: '0', icon: 'home', label: 'Option one', color: 'blue' },
    { value: '1', icon: 'piggyBank', label: 'Option two', color: 'red' },
    { value: '2', icon: 'briefCase', label: 'Option three', color: 'green' },
];

const SELECT_DROPDOWN_OPTIONS = ['Option one', 'Option two', 'Option three'];

const SELECT_DROPDOWN_DEFAULT_VALUE: string =
    SELECT_DROPDOWN_OPTIONS[0] ?? 'Option one';
const SELECT_DROPDOWN_DISABLED_DEFAULT_VALUE: string =
    SELECT_DROPDOWN_OPTIONS[1] ?? 'Option two';

const PICKER_OPTIONS = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Yearly (disabled)', value: 'yearly', enabled: false },
];

export const DropdownSelectors = () => {
    const [value, setValue] = useState<string>('0');
    const [valueWithPlaceholder, setValueWithPlaceholder] = useState<
        string | undefined
    >(undefined);
    const [selectDropdownValue, setSelectDropdownValue] = useState<string>(
        SELECT_DROPDOWN_DEFAULT_VALUE,
    );
    const [pickerValue, setPickerValue] = useState<string | undefined>();
    const [pickerValueTiny, setPickerValueTiny] = useState<
        string | undefined
    >();

    return (
        <YStack gap={48}>
            <YStack gap={24}>
                <YStack gap={6}>
                    <BodyLargeEmphasized>
                        IconSelectDropdown
                    </BodyLargeEmphasized>
                    <BodySmallRegular>
                        Single-select dropdown where each option can show an
                        icon and optional label. The trigger has a minimum
                        width; the dropdown panel matches.
                    </BodySmallRegular>
                </YStack>
                <YStack gap={6}>
                    <BodyMediumEmphasized>With selection</BodyMediumEmphasized>
                    <YStack maxWidth={400}>
                        <IconSelectDropdown
                            options={OPTIONS}
                            value={value}
                            onValueChange={setValue}
                            placeholder="Choose an option"
                        />
                    </YStack>
                </YStack>
                <YStack gap={6}>
                    <BodyMediumEmphasized>
                        Placeholder (no selection)
                    </BodyMediumEmphasized>
                    <YStack maxWidth={400}>
                        <IconSelectDropdown
                            options={OPTIONS}
                            value={valueWithPlaceholder}
                            onValueChange={setValueWithPlaceholder}
                            placeholder="Choose an option"
                        />
                    </YStack>
                </YStack>
                <YStack gap={6}>
                    <BodyMediumEmphasized>Disabled</BodyMediumEmphasized>
                    <YStack maxWidth={400}>
                        <IconSelectDropdown
                            options={OPTIONS}
                            value="1"
                            onValueChange={() => {}}
                            placeholder="Choose an option"
                            disabled
                        />
                    </YStack>
                </YStack>
            </YStack>
            <Separator />
            <YStack gap={6}>
                <BodyLargeEmphasized>SelectDropdown</BodyLargeEmphasized>
                <BodySmallRegular>
                    Single-select dropdown with string options. Updates the
                    selection via `onSelect`.
                </BodySmallRegular>
            </YStack>
            <YStack gap={6}>
                <BodyMediumEmphasized>With selection</BodyMediumEmphasized>
                <YStack maxWidth={400}>
                    <SelectDropdown
                        options={SELECT_DROPDOWN_OPTIONS}
                        defaultValue={SELECT_DROPDOWN_DEFAULT_VALUE}
                        onSelect={(selectedItem) => {
                            if (selectedItem != null) {
                                setSelectDropdownValue(selectedItem);
                            }
                        }}
                    />
                </YStack>
                <BodySmallRegular>
                    Selected: {selectDropdownValue}
                </BodySmallRegular>
            </YStack>
            <YStack gap={6}>
                <BodyMediumEmphasized>No selection</BodyMediumEmphasized>
                <YStack maxWidth={400}>
                    <SelectDropdown
                        options={SELECT_DROPDOWN_OPTIONS}
                        onSelect={() => {}}
                    />
                </YStack>
            </YStack>
            <YStack gap={6}>
                <BodyMediumEmphasized>Disabled</BodyMediumEmphasized>
                <YStack maxWidth={400}>
                    <SelectDropdown
                        options={SELECT_DROPDOWN_OPTIONS}
                        defaultValue={SELECT_DROPDOWN_DISABLED_DEFAULT_VALUE}
                        onSelect={() => {}}
                        disabled
                    />
                </YStack>
            </YStack>
            <Separator />
            <YStack gap={6}>
                <BodyLargeEmphasized>Picker (Deprecated)</BodyLargeEmphasized>
                <BodySmallRegular>
                    Generic picker with label/value options and an optional
                    placeholder. On mobile, tap “Done” to commit `onPressDone`.
                </BodySmallRegular>
            </YStack>
            <YStack maxWidth={400} gap={16}>
                <YStack gap={6}>
                    <BodyMediumRegular>
                        Regular size, with placeholder
                    </BodyMediumRegular>
                    <Picker
                        value={pickerValue}
                        options={PICKER_OPTIONS}
                        placeholder="Select a frequency"
                        onChange={setPickerValue}
                    />
                </YStack>
                <YStack gap={6}>
                    <BodyMediumRegular>
                        Tiny size, without placeholder
                    </BodyMediumRegular>
                    <Picker
                        value={pickerValueTiny}
                        options={PICKER_OPTIONS}
                        onChange={setPickerValueTiny}
                        size="tiny"
                    />
                </YStack>
                <YStack gap={6}>
                    <BodyMediumRegular>Disabled</BodyMediumRegular>
                    <Picker
                        value="monthly"
                        options={PICKER_OPTIONS}
                        placeholder="Select a frequency"
                        onChange={() => {}}
                        disabled
                        size="tiny"
                    />
                </YStack>
            </YStack>
        </YStack>
    );
};
